import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  WhatsApp as WhatsAppIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
  Event as EventIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNotification } from '../../contexts/NotificationContext';
import citaService from '../../services/citaService';
import PageHeader from '../../components/common/PageHeader';

// Funciones helper sin cambios...
const getEstadoColor = (estado) => {
  switch (estado) {
    case 'Programada':
      return 'success'; // Mostrar como confirmada (verde)
    case 'Confirmada':
      return 'success';
    case 'Justificada': // Cambio de Cancelada a Justificada
      return 'error';
    case 'Realizada':
      return 'default';
    case 'No asistió':
      return 'warning';
    case 'Reprogramo':
      return 'info';
    default:
      return 'default';
  }
};

const getEstadoDisplay = (estado) => {
  switch (estado) {
    case 'Programada':
      return 'Confirmada';
    case 'Cancelada':
      return 'Justificada';
    default:
      return estado;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: es });
  } catch (error) {
    return dateString;
  }
};

const formatTime = (timeString) => {
  if (!timeString) return '';
  try {
    return format(new Date(`2000-01-01T${timeString}`), 'h:mm a', { locale: es });
  } catch (error) {
    return timeString;
  }
};

const CitaDetail = () => {
  const { id } = useParams();
  const { showNotification } = useNotification();
  const [cita, setCita] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderSuccess, setReminderSuccess] = useState(false);
  
  // Determinar si es un evento personal
  const isEventoPersonal = cita?.tipo === 'Personal';
  
  // Cargar datos de la cita
  useEffect(() => {
    const fetchCita = async () => {
      setLoading(true);
      try {
        const response = await citaService.getCita(id);
        setCita(response.data);
      } catch (error) {
        console.error('Error fetching appointment:', error);
        showNotification(
          'Error al cargar los datos de la cita',
          'error'
        );
      } finally {
        setLoading(false);
      }
    };
    
    fetchCita();
  }, [id, showNotification]);
  
  // Enviar recordatorio (solo para citas normales)
  const handleSendReminder = async () => {
    // Verificar si es un evento personal
    if (isEventoPersonal) {
      showNotification('No se pueden enviar recordatorios para eventos personales', 'warning');
      return;
    }
    
    setReminderLoading(true);
    setReminderSuccess(false);
    try {
      await citaService.enviarRecordatorio(id);
      showNotification('Recordatorio enviado correctamente', 'success');
      setReminderSuccess(true);
    } catch (error) {
      console.error('Error sending reminder:', error);
      showNotification(
        'Error al enviar el recordatorio',
        'error'
      );
    } finally {
      setReminderLoading(false);
    }
  };
  
  // Confirmar cita
  const handleConfirmCita = async () => {
    try {
      await citaService.updateCita(id, { estado: 'Confirmada' });
      showNotification(isEventoPersonal ? 'Evento confirmado correctamente' : 'Cita confirmada correctamente', 'success');
      // Actualizar la información de la cita localmente
      setCita((prev) => ({ ...prev, estado: 'Confirmada' }));
    } catch (error) {
      console.error('Error confirming appointment:', error);
      showNotification(
        isEventoPersonal ? 'Error al confirmar el evento' : 'Error al confirmar la cita',
        'error'
      );
    }
  };
  
  // Marcar como realizada
  const handleCompleteCita = async () => {
    try {
      await citaService.updateCita(id, { estado: 'Realizada' });
      showNotification(isEventoPersonal ? 'Evento marcado como realizado' : 'Cita marcada como realizada', 'success');
      // Actualizar la información de la cita localmente
      setCita((prev) => ({ ...prev, estado: 'Realizada' }));
    } catch (error) {
      console.error('Error completing appointment:', error);
      showNotification(
        isEventoPersonal ? 'Error al marcar el evento como realizado' : 'Error al marcar la cita como realizada',
        'error'
      );
    }
  };
  
  // Cancelar cita
  const handleCancelCita = async () => {
    try {
      await citaService.updateCita(id, { estado: 'Cancelada' });
      showNotification(isEventoPersonal ? 'Evento cancelado correctamente' : 'Cita cancelada correctamente', 'success');
      // Actualizar la información de la cita localmente
      setCita((prev) => ({ ...prev, estado: 'Cancelada' }));
      setCancelDialogOpen(false);
    } catch (error) {
      console.error('Error canceling appointment:', error);
      showNotification(
        isEventoPersonal ? 'Error al cancelar el evento' : 'Error al cancelar la cita',
        'error'
      );
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={5}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!cita) {
    return (
      <Box>
        <Typography variant="h5" color="error">
          {isEventoPersonal ? 'Evento no encontrado' : 'Cita no encontrada'}
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title={isEventoPersonal ? `Detalle de Evento Personal #${id}` : `Detalle de Cita #${id}`}
        subtitle={`${formatDate(cita.fecha)} | ${formatTime(cita.hora_inicio)} - ${formatTime(cita.hora_fin)}`}
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: isEventoPersonal ? 'Eventos' : 'Citas', to: '/citas' },
          { text: isEventoPersonal ? `Evento #${id}` : `Cita #${id}` }
        ]}
        actionButton={
          <Box>
            {/* Mostrar botón de recordatorio solo para citas normales */}
            {!isEventoPersonal && (cita.estado === 'Programada' || cita.estado === 'Confirmada') && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<WhatsAppIcon />}
                onClick={handleSendReminder}
                disabled={reminderLoading}
                sx={{ mr: 1 }}
              >
                {reminderLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Enviar Recordatorio'
                )}
              </Button>
            )}
            
            {(cita.estado === 'Programada' || cita.estado === 'Confirmada') && (
              <Button
                component={RouterLink}
                to={`/citas/editar/${cita.id}`}
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
              >
                Editar {isEventoPersonal ? 'Evento' : 'Cita'}
              </Button>
            )}
          </Box>
        }
      />
      
      {/* Mostrar alerta de recordatorio enviado solo para citas normales */}
      {!isEventoPersonal && reminderSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Se ha enviado un recordatorio por WhatsApp al paciente.
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Estado y acciones rápidas */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <Typography variant="h6" mr={2}>
                    Estado:
                  </Typography>
                  <Chip
                    label={getEstadoDisplay(cita.estado)}
                    color={getEstadoColor(cita.estado)}
                    size="medium"
                  />
                  {isEventoPersonal && (
                    <Chip
                      label="Evento Personal"
                      color="secondary"
                      size="medium"
                      icon={<PersonIcon />}
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
                
                <Box>
                  {cita.estado === 'Programada' && (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckIcon />}
                      onClick={handleConfirmCita}
                      sx={{ mr: 1 }}
                    >
                      Confirmar
                    </Button>
                  )}
                  
                  {(cita.estado === 'Programada' || cita.estado === 'Confirmada') && (
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<EventIcon />}
                      onClick={handleCompleteCita}
                      sx={{ mr: 1 }}
                    >
                      Marcar como {isEventoPersonal ? 'Realizado' : 'Realizada'}
                    </Button>
                  )}
                  
                  {(cita.estado === 'Programada' || cita.estado === 'Confirmada') && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => setCancelDialogOpen(true)}
                    >
                      Cancelar {isEventoPersonal ? 'Evento' : 'Cita'}
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Información de la cita */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información del {isEventoPersonal ? 'Evento' : 'la Cita'}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Fecha
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDate(cita.fecha)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Horario
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatTime(cita.hora_inicio)} - {formatTime(cita.hora_fin)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Tipo
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {cita.tipo}
                  </Typography>
                </Grid>
                {/* Mostrar especialidad solo para citas normales */}
                {!isEventoPersonal && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Especialidad
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {cita.especialidad?.nombre || 'No especificada'}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {isEventoPersonal ? 'Descripción' : 'Motivo'}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {cita.motivo || 'No especificado'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Notas
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {cita.notas || 'Sin notas adicionales'}
                  </Typography>
                </Grid>
                {/* Mostrar información de recordatorio solo para citas normales */}
                {!isEventoPersonal && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Recordatorio
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {cita.recordatorio_enviado ? 'Enviado' : 'No enviado'}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Información del paciente - Solo para citas normales */}
        {!isEventoPersonal && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" gutterBottom>
                    Información del Paciente
                  </Typography>
                  {cita.paciente_id && (
                    <Button
                      component={RouterLink}
                      to={`/pacientes/${cita.paciente_id}`}
                      variant="text"
                      color="primary"
                    >
                      Ver Perfil Completo
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                {cita.paciente ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Paciente
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {cita.paciente.nombres} {cita.paciente.apellidos}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        DNI
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {cita.paciente.dni}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Historia Clínica
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {cita.paciente.numero_historia}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Teléfono
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {cita.paciente.telefono || 'No registrado'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        WhatsApp
                      </Typography>
                      <Typography variant="body1" gutterBottom display="flex" alignItems="center">
                        {cita.paciente.whatsapp || 'No registrado'}
                        {cita.paciente.whatsapp && (
                          <IconButton
                            size="small"
                            color="primary"
                            href={`https://wa.me/${cita.paciente.whatsapp.replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            sx={{ ml: 1 }}
                          >
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Email
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {cita.paciente.email || 'No registrado'}
                      </Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body1" color="textSecondary">
                    Información del paciente no disponible
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        
        {/* Para eventos personales, mostrar un segundo panel con detalles adicionales */}
        {isEventoPersonal && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Detalles del Evento Personal
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Tipo de Evento
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      Evento Personal
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Visibilidad
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      Solo visible para el especialista
                    </Typography>
                  </Grid>
                  {/* Aquí puedes añadir otros campos específicos para eventos personales */}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
        
        {/* Información del especialista */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6" gutterBottom>
                  Información del Especialista
                </Typography>
                {cita.especialista && (
                  <Button
                    component={RouterLink}
                    to={`/especialistas/${cita.especialista_id}`}
                    variant="text"
                    color="primary"
                  >
                    Ver Perfil Completo
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              {cita.especialista ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Especialista
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {cita.especialista.usuario ? `${cita.especialista.usuario.nombre} ${cita.especialista.usuario.apellidos}` : 'Nombre no disponible'}
                    </Typography>
                  </Grid>
                  {!isEventoPersonal && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Especialidad
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {cita.especialidad?.nombre || 'No especificada'}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      DNI
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {cita.especialista.dni || 'No registrado'}
                    </Typography>
                  </Grid>
                  {!isEventoPersonal && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Colegiatura
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {cita.especialista.num_colegiatura || 'No registrado'}
                      </Typography>
                    </Grid>
                  )}
                  {cita.especialista.whatsapp && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        WhatsApp
                      </Typography>
                      <Typography variant="body1" gutterBottom display="flex" alignItems="center">
                        {cita.especialista.whatsapp}
                        <IconButton
                          size="small"
                          color="primary"
                          href={`https://wa.me/${cita.especialista.whatsapp.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          sx={{ ml: 1 }}
                        >
                          <WhatsAppIcon fontSize="small" />
                        </IconButton>
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography variant="body1" color="textSecondary">
                  Información del especialista no disponible
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Mostrar programa y etapa solo para citas normales */}
        {!isEventoPersonal && (
          <>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Programa
              </Typography>
              <Typography variant="body1" gutterBottom>
                {cita.programa?.nombre || 'No especificado'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Etapa de Tratamiento
              </Typography>
              <Typography variant="body1" gutterBottom>
                {cita.etapa?.nombre || 'No especificada'}
                {cita.etapa && cita.etapa.duracion_estimada && (
                  <Chip 
                    label={`${cita.etapa.duracion_estimada} sesión(es)`} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
            </Grid>
          </>
        )}

        {/* Recordatorios - Solo para citas normales */}
        {!isEventoPersonal && cita.recordatorios && cita.recordatorios.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Historial de Recordatorios
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  {cita.recordatorios.map((recordatorio, index) => (
                    <Grid item xs={12} key={index}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Recordatorio {index + 1} - {recordatorio.tipo}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          Estado: <Chip 
                            label={recordatorio.estado} 
                            size="small" 
                            color={recordatorio.estado === 'Enviado' ? 'success' : recordatorio.estado === 'Fallido' ? 'error' : 'warning'} 
                          />
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          Fecha programada: {recordatorio.fecha_programada ? formatDate(recordatorio.fecha_programada) : 'No especificada'}
                        </Typography>
                        {recordatorio.fecha_envio && (
                          <Typography variant="body2" gutterBottom>
                            Fecha de envío: {formatDate(recordatorio.fecha_envio)}
                          </Typography>
                        )}
                        <Typography variant="body2" gutterBottom>
                          Contenido: {recordatorio.contenido}
                        </Typography>
                        {recordatorio.error_mensaje && (
                          <Typography variant="body2" color="error">
                            Error: {recordatorio.error_mensaje}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
      
      {/* Diálogo de confirmación para cancelar */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
      >
        <DialogTitle>Cancelar {isEventoPersonal ? 'Evento' : 'Cita'}</DialogTitle>
        <DialogContent>
        
          <DialogContentText>
            {isEventoPersonal 
              ? `¿Estás seguro de que deseas cancelar el evento personal del ${formatDate(cita.fecha)}?`
              : `¿Estás seguro de que deseas cancelar la cita del ${formatDate(cita.fecha)} para ${cita.paciente ? `${cita.paciente.nombres} ${cita.paciente.apellidos}` : 'el paciente'}?`
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} color="primary">
            No, Mantener
          </Button>
          <Button onClick={handleCancelCita} color="error">
            Sí, Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CitaDetail;